import { useEffect, useState } from "react";
import { searchThreadsApi } from "../api/client";
import type { SearchThread, SearchProviderName, SettingsMode } from "../types";
import { PROVIDER_LABELS } from "./StagePill";
import "./SearchThreadEditor.css";

const ALL_SEARCH_PROVIDERS: SearchProviderName[] = [
  "searxng", "ddgs", "tavily", "brave", "brave_ai", "exa", "google_places",
];

let nextTempId = 1;

export default function SearchThreadEditor({ mode }: { mode: SettingsMode }) {
  const [threads, setThreads] = useState<SearchThread[] | null>(null);
  const [minResults, setMinResults] = useState(3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setThreads(null);
    searchThreadsApi.get(mode).then((r) => {
      setThreads(r.threads);
      setMinResults(r.min_results);
    });
  }, [mode]);

  const persist = async (next: SearchThread[], nextMin = minResults) => {
    setThreads(next);
    setMinResults(nextMin);
    setSaving(true);
    setError(null);
    try {
      const r = await searchThreadsApi.save(
        mode,
        next.map(({ rank, execution, providers }) => ({ rank, execution, providers })),
        nextMin
      );
      setThreads(r.threads);
      setMinResults(r.min_results);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save search threads");
    } finally {
      setSaving(false);
    }
  };

  const updateThread = (id: string, patch: Partial<SearchThread>) => {
    if (!threads) return;
    persist(threads.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const toggleProvider = (id: string, name: SearchProviderName, on: boolean) => {
    if (!threads) return;
    const t = threads.find((t) => t.id === id);
    if (!t) return;
    const providers = on ? [...t.providers, name] : t.providers.filter((p) => p !== name);
    updateThread(id, { providers });
  };

  const moveProvider = (id: string, index: number, dir: -1 | 1) => {
    if (!threads) return;
    const t = threads.find((t) => t.id === id);
    if (!t) return;
    const target = index + dir;
    if (target < 0 || target >= t.providers.length) return;
    const providers = [...t.providers];
    [providers[index], providers[target]] = [providers[target], providers[index]];
    updateThread(id, { providers });
  };

  const addThread = () => {
    if (!threads) return;
    const rank = threads.length ? Math.max(...threads.map((t) => t.rank)) + 10 : 0;
    persist([...threads, { id: `tmp-${nextTempId++}`, rank, execution: "serial", providers: [] }]);
  };

  const removeThread = (id: string) => {
    if (!threads) return;
    persist(threads.filter((t) => t.id !== id));
  };

  if (!threads) return <div className="dim">Loading…</div>;

  return (
    <div className="col search-thread-editor">
      <div className="dim" style={{ fontSize: 12 }}>
        Search runs in rank order — lower rank first, equal ranks run at the same time. Each
        thread is a set of providers, tried one after another ("serial", stopping once the
        minimum below is met) or fired all at once ("parallel"). Search stops scheduling more
        threads once the minimum is met across everything run so far.
      </div>

      {error && <div className="tag tag-error">{error}</div>}
      {threads.length === 0 && <div className="dim">No search threads configured.</div>}

      {[...threads].sort((a, b) => a.rank - b.rank).map((t) => (
        <div key={t.id} className="search-thread-row">
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <label className="form-label" style={{ margin: 0 }}>
              Rank
              <input
                type="number"
                className="input input-sm"
                value={t.rank}
                onChange={(e) => updateThread(t.id, { rank: parseInt(e.target.value, 10) || 0 })}
                style={{ width: 64, marginLeft: 6 }}
              />
            </label>
            <label className="row" style={{ gap: 4 }}>
              <input
                type="radio"
                name={`exec-${t.id}`}
                checked={t.execution === "serial"}
                onChange={() => updateThread(t.id, { execution: "serial" })}
              />
              Serial
            </label>
            <label className="row" style={{ gap: 4 }}>
              <input
                type="radio"
                name={`exec-${t.id}`}
                checked={t.execution === "parallel"}
                onChange={() => updateThread(t.id, { execution: "parallel" })}
              />
              Parallel
            </label>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              style={{ marginLeft: "auto" }}
              onClick={() => removeThread(t.id)}
            >
              Remove
            </button>
          </div>
          {t.execution === "serial" ? (
            <>
              {t.providers.length > 0 && (
                <div className="col search-thread-order" style={{ gap: 4, marginTop: 4 }}>
                  <div className="dim" style={{ fontSize: 12 }}>
                    Search order (tried top to bottom, stops once the minimum below is met):
                  </div>
                  {t.providers.map((name, i) => (
                    <div key={name} className="row" style={{ gap: 6, alignItems: "center" }}>
                      <span className="dim" style={{ width: 18, textAlign: "right" }}>{i + 1}.</span>
                      <span style={{ flex: 1 }}>{PROVIDER_LABELS[name] ?? name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={i === 0}
                        onClick={() => moveProvider(t.id, i, -1)}
                        aria-label={`Move ${name} up`}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={i === t.providers.length - 1}
                        onClick={() => moveProvider(t.id, i, 1)}
                        aria-label={`Move ${name} down`}
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => toggleProvider(t.id, name, false)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="row search-thread-providers" style={{ flexWrap: "wrap", gap: 10, marginTop: 4 }}>
                {ALL_SEARCH_PROVIDERS.filter((name) => !t.providers.includes(name)).map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => toggleProvider(t.id, name, true)}
                  >
                    + {PROVIDER_LABELS[name] ?? name}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="row search-thread-providers" style={{ flexWrap: "wrap", gap: 10 }}>
              {ALL_SEARCH_PROVIDERS.map((name) => (
                <label key={name} className="row" style={{ gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={t.providers.includes(name)}
                    onChange={(e) => toggleProvider(t.id, name, e.target.checked)}
                  />
                  {PROVIDER_LABELS[name] ?? name}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}

      <button type="button" className="btn btn-sm" onClick={addThread} disabled={saving}>
        + Add thread
      </button>

      <div className="col" style={{ marginTop: 8 }}>
        <label className="form-label">Minimum results</label>
        <input
          type="number"
          className="input input-sm"
          style={{ width: 80 }}
          value={minResults}
          min={1}
          onChange={(e) => persist(threads, parseInt(e.target.value, 10) || 1)}
        />
        <div className="dim" style={{ fontSize: 12 }}>
          Stops search once this many deduped results are found across all threads; also used
          as each serial thread's own threshold before it tries its next provider.
        </div>
      </div>
    </div>
  );
}
